package ru.chm.gradereport

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import kotlinx.coroutines.launch
import ru.chm.gradereport.data.GradeApi
import ru.chm.gradereport.model.CourseDto
import ru.chm.gradereport.model.UserDto
import ru.chm.gradereport.model.UserRole
import ru.chm.gradereport.navigation.AppPath
import ru.chm.gradereport.navigation.parseAppHashPath
import ru.chm.gradereport.navigation.toHashPath
import ru.chm.gradereport.platform.addAppHashPathListener
import ru.chm.gradereport.platform.addSystemThemeListener
import ru.chm.gradereport.platform.applyDocumentChrome
import ru.chm.gradereport.platform.getAppHashPath
import ru.chm.gradereport.platform.isSystemInDarkTheme
import ru.chm.gradereport.platform.loadAuthToken
import ru.chm.gradereport.platform.loadThemeMode
import ru.chm.gradereport.platform.saveThemeMode
import ru.chm.gradereport.platform.setAppHashPath
import ru.chm.gradereport.ui.components.LoadingBox
import ru.chm.gradereport.ui.screens.course.CourseScreen
import ru.chm.gradereport.ui.screens.home.HomeScreen
import ru.chm.gradereport.ui.screens.login.LoginScreen
import ru.chm.gradereport.ui.screens.publiccheckin.PublicCheckInScreen
import ru.chm.gradereport.ui.screens.publichomework.PublicHomeworkScreen
import ru.chm.gradereport.ui.screens.publicsheet.PublicSheetScreen
import ru.chm.gradereport.ui.screens.publicstudent.StudentPublicScreen
import ru.chm.gradereport.ui.screens.reviewers.ReviewersScreen
import ru.chm.gradereport.ui.theme.GradeReportTheme
import ru.chm.gradereport.ui.theme.GradeTheme
import ru.chm.gradereport.ui.theme.LocalThemeController
import ru.chm.gradereport.ui.theme.ThemeController
import ru.chm.gradereport.ui.theme.ThemeMode

private sealed interface AppRoute {
    data object Login : AppRoute
    data object Home : AppRoute
    data object Reviewers : AppRoute
    data class Course(val course: CourseDto) : AppRoute
    data class Public(val token: String) : AppRoute
    data class PublicHomework(val token: String) : AppRoute
    data class PublicStudent(val token: String, val studentUid: String) : AppRoute
    data class CheckIn(val token: String) : AppRoute
}

private fun AppRoute.toAppPath(): AppPath = when (this) {
    AppRoute.Login -> AppPath.Login
    AppRoute.Home -> AppPath.Home
    AppRoute.Reviewers -> AppPath.Reviewers
    is AppRoute.Course -> AppPath.Course(course.id)
    is AppRoute.Public -> AppPath.Public(token)
    is AppRoute.PublicHomework -> AppPath.PublicHomework(token)
    is AppRoute.PublicStudent -> AppPath.PublicStudent(token, studentUid)
    is AppRoute.CheckIn -> AppPath.CheckIn(token)
}

@Composable
@Preview
fun App() {
    var themeMode by remember {
        mutableStateOf(ThemeMode.fromStorage(loadThemeMode()).also { mode ->
            // Persist default so first visit is explicitly «Системная».
            if (loadThemeMode().isNullOrBlank()) {
                saveThemeMode(mode.storageKey)
            }
        })
    }
    var systemDark by remember { mutableStateOf(isSystemInDarkTheme()) }

    DisposableEffect(Unit) {
        val unsubscribe = addSystemThemeListener { systemDark = it }
        onDispose { unsubscribe() }
    }

    val darkTheme = when (themeMode) {
        ThemeMode.Dark -> true
        ThemeMode.Light -> false
        ThemeMode.System -> systemDark
    }

    LaunchedEffect(darkTheme) {
        applyDocumentChrome(darkTheme)
    }

    fun setThemeMode(mode: ThemeMode) {
        themeMode = mode
        saveThemeMode(mode.storageKey)
    }

    GradeReportTheme(darkTheme = darkTheme) {
        val surface = GradeTheme.colors.surface
        Surface(
            modifier = Modifier.fillMaxSize().background(surface),
            color = surface,
        ) {
            val api = remember { GradeApi() }
            var route by remember { mutableStateOf<AppRoute>(AppRoute.Login) }
            var user by remember { mutableStateOf<UserDto?>(null) }
            var bootstrapping by remember { mutableStateOf(true) }
            var applyingUrl by remember { mutableStateOf(false) }
            val scope = rememberCoroutineScope()

            fun go(next: AppRoute) {
                route = next
            }

            suspend fun applyPath(path: String, authedUser: UserDto?) {
                applyingUrl = true
                try {
                    when (val deep = parseAppHashPath(path)) {
                        null, AppPath.Home -> {
                            if (authedUser != null) go(AppRoute.Home) else go(AppRoute.Login)
                        }
                        AppPath.Login -> {
                            if (authedUser != null) go(AppRoute.Home) else go(AppRoute.Login)
                        }
                        AppPath.Reviewers -> {
                            when {
                                authedUser == null -> go(AppRoute.Login)
                                authedUser.role != UserRole.ADMIN -> go(AppRoute.Home)
                                else -> go(AppRoute.Reviewers)
                            }
                        }
                        is AppPath.Course -> {
                            if (authedUser == null) {
                                go(AppRoute.Login)
                            } else {
                                runCatching { api.getCourse(deep.courseId) }
                                    .onSuccess { go(AppRoute.Course(it)) }
                                    .onFailure { go(AppRoute.Home) }
                            }
                        }
                        is AppPath.Public -> go(AppRoute.Public(deep.token))
                        is AppPath.PublicHomework -> go(AppRoute.PublicHomework(deep.token))
                        is AppPath.PublicStudent -> go(
                            AppRoute.PublicStudent(deep.token, deep.studentUid),
                        )
                        is AppPath.CheckIn -> go(AppRoute.CheckIn(deep.token))
                    }
                } finally {
                    applyingUrl = false
                }
            }

            LaunchedEffect(Unit) {
                val initialPath = getAppHashPath()
                when (val deep = parseAppHashPath(initialPath)) {
                    is AppPath.Public -> {
                        go(AppRoute.Public(deep.token))
                        bootstrapping = false
                        return@LaunchedEffect
                    }
                    is AppPath.PublicHomework -> {
                        go(AppRoute.PublicHomework(deep.token))
                        bootstrapping = false
                        return@LaunchedEffect
                    }
                    is AppPath.PublicStudent -> {
                        go(AppRoute.PublicStudent(deep.token, deep.studentUid))
                        bootstrapping = false
                        return@LaunchedEffect
                    }
                    is AppPath.CheckIn -> {
                        go(AppRoute.CheckIn(deep.token))
                        bootstrapping = false
                        return@LaunchedEffect
                    }
                    else -> Unit
                }
                val token = loadAuthToken()
                if (token != null) {
                    api.setToken(token)
                    try {
                        user = api.me()
                        applyPath(initialPath.ifBlank { "/home" }, user)
                    } catch (_: Throwable) {
                        api.setToken(null)
                        user = null
                        go(AppRoute.Login)
                    }
                } else {
                    go(AppRoute.Login)
                }
                bootstrapping = false
            }

            LaunchedEffect(route, bootstrapping, applyingUrl) {
                if (bootstrapping || applyingUrl) return@LaunchedEffect
                setAppHashPath(route.toAppPath().toHashPath())
            }

            DisposableEffect(user) {
                val unsubscribe = addAppHashPathListener { path ->
                    scope.launch {
                        applyPath(path, user)
                    }
                }
                onDispose { unsubscribe() }
            }

            if (bootstrapping) {
                LoadingBox()
                return@Surface
            }

            CompositionLocalProvider(
                LocalThemeController provides ThemeController(
                    mode = themeMode,
                    onModeChange = ::setThemeMode,
                ),
            ) {
                when (val current = route) {
                    AppRoute.Login -> LoginScreen(api = api) { response ->
                        user = response.user
                        go(AppRoute.Home)
                    }

                    AppRoute.Home -> {
                        val currentUser = user
                        if (currentUser == null) {
                            go(AppRoute.Login)
                        } else {
                            HomeScreen(
                                api = api,
                                user = currentUser,
                                onOpenCourse = { go(AppRoute.Course(it)) },
                                onOpenReviewers = { go(AppRoute.Reviewers) },
                                onLogout = {
                                    scope.launch {
                                        api.logout()
                                        user = null
                                        go(AppRoute.Login)
                                    }
                                },
                            )
                        }
                    }

                    AppRoute.Reviewers -> ReviewersScreen(api = api)

                    is AppRoute.Course -> CourseScreen(
                        api = api,
                        course = current.course,
                        editable = true,
                        isAdmin = user?.role == UserRole.ADMIN,
                        onOpenPublic = { token -> go(AppRoute.Public(token)) },
                        onOpenPublicHomework = { token -> go(AppRoute.PublicHomework(token)) },
                        onOpenPublicStudent = { token, studentUid ->
                            go(AppRoute.PublicStudent(token, studentUid))
                        },
                    )

                    is AppRoute.Public -> PublicSheetScreen(
                        api = api,
                        publicToken = current.token,
                    )

                    is AppRoute.PublicHomework -> PublicHomeworkScreen(
                        api = api,
                        homeworkToken = current.token,
                    )

                    is AppRoute.PublicStudent -> StudentPublicScreen(
                        api = api,
                        publicToken = current.token,
                        studentUid = current.studentUid,
                    )

                    is AppRoute.CheckIn -> PublicCheckInScreen(
                        api = api,
                        token = current.token,
                    )
                }
            }
        }
    }
}
